/* eslint-disable react/no-danger */
import classNames from 'classnames';
import { useMemo } from 'react';

import { addNonBreakingSpaces, cleanExternalLinks } from '../../lib/utils';

import styles from '../../../styles/blocks/text.module.css';

interface TextBlockProps {
    title?: string | null;
    subtitle?: string | null;
    body?: string | null;
    isSummary?: boolean;
    isSection?: boolean;
    isSmaller?: boolean;
    hasLettrine?: boolean;
    className?: string | null;
}

function TextBlock({
    title = null,
    subtitle = null,
    body = null,
    isSummary = false,
    isSection = false,
    isSmaller = false,
    hasLettrine = false,
    className = null,
}: TextBlockProps) {
    const finalBody = useMemo(() => {
        // Older articles already contain their own lettrine span, often inside a <p>
        if (!hasLettrine || body === null || /class=["']lettrine["']/.test(body)) {
            return body;
        }
        // Tags and HTML entities (e.g. &Agrave;) are single tokens so a letter is never split
        const tokens = body.match(/<[^>]*>|&#?\w+;|[^<&]|&/g) ?? [];
        const index = tokens.findIndex((token) => !token.startsWith('<') && token.trim() !== '');
        if (index === -1) {
            return body;
        }
        // Keep an elided apostrophe with its letter (L’été)
        const withApostrophe = /^(['’]|&rsquo;|&#39;|&apos;)$/.test(tokens[index + 1] ?? '');
        const letter = withApostrophe ? tokens[index] + tokens[index + 1] : tokens[index];
        tokens.splice(index, withApostrophe ? 2 : 1, `<span class="lettrine">${letter}</span>`);
        return tokens.join('');
    }, [body, hasLettrine]);

    return (
        <div
            className={classNames({
                [styles.container]: true,
                [styles.isSummary]: isSummary,
                [styles.isSmaller]: isSmaller,
                [styles.isSection]: isSection,
                [className]: className !== null,
            })}
        >
            <div className={classNames([styles.inner, { 'page-inner': isSection }])}>
                {title !== null ? (
                    <h2
                        className={classNames([
                            styles.title,
                            {
                                [styles.withSubtitle]: subtitle !== null,
                            },
                        ])}
                    >
                        {addNonBreakingSpaces(cleanExternalLinks(title)) as React.ReactNode}
                    </h2>
                ) : null}
                {subtitle !== null ? (
                    <h3 className={styles.subtitle}>
                        {addNonBreakingSpaces(cleanExternalLinks(subtitle)) as React.ReactNode}
                    </h3>
                ) : null}
                {finalBody !== null ? (
                    <div
                        className={styles.body}
                        dangerouslySetInnerHTML={{ __html: cleanExternalLinks(finalBody) }}
                    />
                ) : null}
            </div>
        </div>
    );
}

export default TextBlock;
